import { MessageBarType } from '@fluentui/react';
import { defaultPalette, IMessageBarMessage, RowMeta } from '@h2oai/ui-kit';

import {
  AccessModifier,
  ActivePermission,
  BackfillOptions,
  DraftToFeatureSetRequest,
  FeatureDataType,
  FeatureSchemaDraft,
  JobStatus,
  JobType,
  PermissionType,
  ResourceType,
  SortDirection,
  TransformationType,
  UserBasicInfo,
  VersionedId,
} from './gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { FeatureSetFlow } from './gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
export interface DetailField {
  label: string;
  value?: string;
  color?: string;
}

export enum ContentsTitle {
  home = 'Home',
  project = 'Project',
  createProject = 'Create Project',
  editProject = 'Edit Project',
  registerFeatureSet = 'Register Feature Set',
  selectCreateProject = 'Select or Create Project',
  projects = 'Projects',
  allProjects = 'All Projects',
  myProjects = 'My Projects',
  featureSet = 'Feature Set',
  featureSets = 'Feature Sets',
  featureSetList = 'Feature Set List',
  allFeatureSets = 'All Feature Sets',
  myFeatureSets = 'My Feature Sets',
  accessControl = 'Access Control',
  projectPermissions = 'Project Permissions',
  featureSetPermissions = 'Feature Set Permissions',
  features = 'Features',
  dashboard = 'Dashboard',
  managePermissions = 'Managed Permissions',
  myPermissions = 'My Permissions',
  editFeature = 'Edit Feature',
  editFeatureSet = 'Edit Feature Set',
  projectAccess = 'Project Access',
  featureSetAccess = 'Feature Set Access',
  requestAccess = 'Request Access',
  ongoingRegistration = 'Ongoing Registration',
  addSchema = 'Add Schema',
  jobs = 'Jobs',
  allJobs = 'All Jobs',
  runningJobs = 'Running Jobs',
  failedJobs = 'Failed Jobs',
  pendingJobs = 'Pending Jobs',
  successfulJobs = 'Successful Jobs',
  uploadArtifact = 'Upload Artifact',
  metaData = 'Meta Data',
  preview = 'Preview',
  statistics = 'Statistics',
  artifacts = 'Artifacts',
  setting = 'Setting',
  personalAccessToken = 'Personal Access Token',
  create = 'Create',
  createPersonalAccessToken = 'Create Personal Access Token',
  pendingReviews = 'Pending Reviews',
  pendingFeatureSets = 'Pending Feature Sets',
  ingestHistory = 'Ingest History',
  ingest = 'Ingest',
  retrieve = 'Retrieve',
  download = 'Download',
  help = 'Help',
  helpAndDocumentation = 'Help & Documentation',
  onlineMaterialize = 'Online Materialize',
  codeSnippet = 'Code Snippet',
  scheduledTasks = 'Scheduled Tasks',
  schedule = 'Schedule',
  versions = 'Versions',
  createNewMajorVersion = 'Create New Major Version',
  keyDetails = 'Key Details',
  reviews = 'Reviews',
  manual = 'Manual',
  editor = 'Editor',
  myAccess = 'My Access',
  accessList = 'Access List',
  grantAccess = 'Grant Access',
  promote = 'Promote',
  raw = 'Raw',
  table = 'Table',
  parentFeatureSets = 'Parent Feature Sets',
  derivedFeatureSets = 'Derived Feature Sets',
}

export enum CommonImageSrc {
  logo512 = '/logo512.png',
}

export const activePermissionNameMap = new Map<ActivePermission | undefined, string>([
  [ActivePermission.ACTIVE_PERMISSION_OWNER, 'Owner'],
  [ActivePermission.ACTIVE_PERMISSION_EDITOR, 'Editor'],
  [ActivePermission.ACTIVE_PERMISSION_CONSUMER, 'Consumer'],
  [ActivePermission.ACTIVE_PERMISSION_SENSITIVE_CONSUMER, 'Sensitive Consumer'],
  [ActivePermission.ACTIVE_PERMISSION_VIEWER, 'Viewer'],
  [ActivePermission.ACTIVE_PERMISSION_NONE, 'No Permission'],
]);

// don't change the order since the order is the permission hierarchy
export const permissionTypeNameMap = new Map<PermissionType, string>([
  [PermissionType.Viewer, 'Viewer'],
  [PermissionType.Consumer, 'Consumer'],
  [PermissionType.SensitiveConsumer, 'Sensitive Consumer'],
  [PermissionType.Editor, 'Editor'],
  [PermissionType.Owner, 'Owner'],
]);

export const defaultPageSize = 50;

export const defaultPanelWidth = '668px';
export const narrowPanelWidth = '450px';

export const buttonStylesPrimaryPanel = { root: { width: 132 }, label: { textAlign: 'center' } };

export const FeatureDataTypeWithExtraTypes = {
  ...FeatureDataType,
  Array: 'Array',
  Struct: 'Struct',
};

export interface FeatureSchemaDraftWithDepth extends FeatureSchemaDraft {
  depthWithHasNextSibling?: boolean[];
  __meta__?: RowMeta;
}

export interface SortInfo {
  field?: string;
  direction?: SortDirection;
}

export enum NotificationType {
  DRAFT_EXTRACT_SCHEMA_FINISHED_EVENT = 'DRAFT_EXTRACT_SCHEMA_FINISHED_EVENT',
  DRAFT_EXTRACT_SCHEMA_FAILED_EVENT = 'DRAFT_EXTRACT_SCHEMA_FAILED_EVENT',
  JOB_FINISHED_EVENT = 'JOB_FINISHED_EVENT',
  PERMISSION_PROJECT_REQUEST_EVENT = 'PERMISSION_PROJECT_REQUEST_EVENT',
  PERMISSION_FEATURE_SET_REQUEST_EVENT = 'PERMISSION_FEATURE_SET_REQUEST_EVENT',
  PERMISSION_PROJECT_GRANTED_EVENT = 'PERMISSION_PROJECT_GRANTED_EVENT',
  PERMISSION_FEATURE_SET_GRANTED_EVENT = 'PERMISSION_FEATURE_SET_GRANTED_EVENT',
  PERMISSION_PROJECT_REJECTED_EVENT = 'PERMISSION_PROJECT_REJECTED_EVENT',
  PERMISSION_FEATURE_SET_REJECTED_EVENT = 'PERMISSION_FEATURE_SET_REJECTED_EVENT',
  PERMISSION_PROJECT_REVOKED_EVENT = 'PERMISSION_PROJECT_REVOKED_EVENT',
  PERMISSION_FEATURE_SET_REVOKED_EVENT = 'PERMISSION_FEATURE_SET_REVOKED_EVENT',
  FEATURE_SET_REVIEW_REQUEST_EVENT = 'FEATURE_SET_REVIEW_REQUEST_EVENT',
  FEATURE_SET_REVIEW_APPROVED_EVENT = 'FEATURE_SET_REVIEW_APPROVED_EVENT',
  FEATURE_SET_REVIEW_REJECTED_EVENT = 'FEATURE_SET_REVIEW_REJECTED_EVENT',
  PROJECT_UPDATED_EVENT = 'PROJECT_UPDATED_EVENT',
  FEATURE_SET_UPDATED_EVENT = 'FEATURE_SET_UPDATED_EVENT',
}

export interface NotificationEvent {
  creatorId?: UserBasicInfo;
  draftId?: number;
  eventTime?: string;
  jobStatus?: JobStatus;
  message?: string;
  permissionId?: number;
  resourceId?: string;
  resourceName?: string;
  type?: NotificationType;
}

export const notificationTypeNameMap = new Map<NotificationType | undefined, Partial<IMessageBarMessage>>([
  [
    NotificationType.DRAFT_EXTRACT_SCHEMA_FAILED_EVENT,
    { title: 'Draft Extract Schema Failed', messageBarType: MessageBarType.error },
  ],
  [
    NotificationType.DRAFT_EXTRACT_SCHEMA_FINISHED_EVENT,
    { title: 'Draft Extract Schema Finished', messageBarType: MessageBarType.success },
  ],
  [
    NotificationType.FEATURE_SET_REVIEW_APPROVED_EVENT,
    { title: 'Feature Set Review Approved', messageBarType: MessageBarType.success },
  ],
  [
    NotificationType.FEATURE_SET_REVIEW_REJECTED_EVENT,
    { title: 'Feature Set Review Rejected', messageBarType: MessageBarType.blocked },
  ],
  [
    NotificationType.FEATURE_SET_REVIEW_REQUEST_EVENT,
    { title: 'Feature Set Review Request', messageBarType: MessageBarType.info },
  ],
  [NotificationType.FEATURE_SET_UPDATED_EVENT, { title: 'Feature Set Updated', messageBarType: MessageBarType.info }],
  [NotificationType.JOB_FINISHED_EVENT, { title: 'Job Finished', messageBarType: MessageBarType.success }],
  [
    NotificationType.PERMISSION_FEATURE_SET_GRANTED_EVENT,
    { title: 'Permission Feature Set Granted', messageBarType: MessageBarType.success },
  ],
  [
    NotificationType.PERMISSION_FEATURE_SET_REJECTED_EVENT,
    { title: 'Permission Feature Set Rejected', messageBarType: MessageBarType.blocked },
  ],
  [
    NotificationType.PERMISSION_FEATURE_SET_REQUEST_EVENT,
    { title: 'Permission Feature Set Request', messageBarType: MessageBarType.info },
  ],
  [
    NotificationType.PERMISSION_FEATURE_SET_REVOKED_EVENT,
    { title: 'Permission Feature Set Revoked', messageBarType: MessageBarType.blocked },
  ],
  [
    NotificationType.PERMISSION_PROJECT_GRANTED_EVENT,
    { title: 'Permission Project Granted', messageBarType: MessageBarType.success },
  ],
  [
    NotificationType.PERMISSION_PROJECT_REJECTED_EVENT,
    { title: 'Permission Project Rejected', messageBarType: MessageBarType.blocked },
  ],
  [
    NotificationType.PERMISSION_PROJECT_REVOKED_EVENT,
    { title: 'Permission Project Revoked', messageBarType: MessageBarType.blocked },
  ],
]);

export const featureSetFlowNameMap = new Map<FeatureSetFlow, string>([
  [FeatureSetFlow.ONLINE_ONLY, 'Online only'],
  [FeatureSetFlow.OFFLINE_ONLY, 'Offline only'],
  [FeatureSetFlow.OFFLINE_ONLINE_MANUAL, 'Offline & Online, manual synchronization'],
  [FeatureSetFlow.OFFLINE_ONLINE_AUTOMATIC, 'Offline & Online, automatic synchronization'],
]);

export const jobStatusColorMap = new Map<JobStatus, string>([
  [JobStatus.Created, defaultPalette.blue100],
  [JobStatus.Submitted, defaultPalette.blue100],
  [JobStatus.Running, defaultPalette.blue100],
  [JobStatus.SubmissionFailed, defaultPalette.red100],
  [JobStatus.Failed, defaultPalette.red100],
  [JobStatus.Success, defaultPalette.green100],
  [JobStatus.Pending, defaultPalette.yellow100],
  [JobStatus.PendingRerun, defaultPalette.yellow100],
  [JobStatus.Failing, defaultPalette.red100],
  [JobStatus.Cancelling, defaultPalette.yellow100],
  [JobStatus.Cancelled, defaultPalette.red100],
]);

export const jobTypeNameMap = new Map<JobType | undefined, string>([
  [JobType.Backfill, JobType.Backfill],
  [JobType.ComputeRecommendationClassifiers, 'Compute Recommendation Classifiers'],
  [JobType.ComputeStatistics, 'Compute Statistics'],
  [JobType.ExtractSchema, 'Extract Schema'],
  [JobType.Ingest, JobType.Ingest],
  [JobType.MaterializationOnline, 'Materialization Online'],
  [JobType.Retrieve, JobType.Retrieve],
  [JobType.Revert, JobType.Revert],
  [JobType.OptimizeStorage, 'Optimize Storage'],
]);

export const jobStatusNameMap = new Map<JobStatus | undefined, string>([
  [JobStatus.Created, JobStatus.Created],
  [JobStatus.Failed, JobStatus.Failed],
  [JobStatus.Failing, JobStatus.Failing],
  [JobStatus.Pending, JobStatus.Pending],
  [JobStatus.PendingRerun, 'Pending Rerun'],
  [JobStatus.Running, JobStatus.Running],
  [JobStatus.SubmissionFailed, 'Submission Failed'],
  [JobStatus.Submitted, JobStatus.Submitted],
  [JobStatus.Success, JobStatus.Success],
  [JobStatus.Cancelling, JobStatus.Cancelling],
  [JobStatus.Cancelled, JobStatus.Cancelled],
]);

export enum DatetimeFormatType {
  spark = 'spark',
}

export enum SchemaErrorMessage {
  uniqueFeatureName = 'The feature name should be unique',
  requiredFeatureName = 'The feature name is required',
  requiredFeatureDataType = 'The feature data type is required',
  requiredFeature = 'At least one feature is required',
  validationError = 'The feature list has validation errors. Please review the feature list again',
}

export enum FeatureSetAction {
  register = 'register',
  newVersion = 'new version',
  draft = 'draft',
}

export interface DraftToFeatureSet extends DraftToFeatureSetRequest {
  affectedFeatures?: string[];
  reason?: string;
  primaryKey?: string[];
  usePrimaryKeyFromPreviousVersion?: boolean;
  backfillOptions?: BackfillOptions;
  partitionBy?: string[];
  usePartitionByFromPreviousVersion?: boolean;
  featureSetId?: string;
  featureSetVersion?: string;
}

export interface NameValue {
  name?: string;
  dataType?: string;
  value?: string;
}

export interface AccessInfo {
  name?: string;
  email?: string;
  permission?: PermissionType;
  resourceType?: ResourceType;
  permissionName?: string;
}

export interface TransformationInfo {
  projectId?: string;
  featureSetIdVersion?: VersionedId;
  file?: File;
  md5Checksum?: string;
  transformationType?: TransformationType;
}

export interface FileMd5 {
  file?: File;
  md5Checksum?: string;
}

export interface DataSourceFileInfo {
  parents?: string[];
  fileMd5s?: FileMd5[];
  name?: string;
}

export const dataSourceUploadFileFieldMap = new Map([
  ['csv', 'path'],
  ['json', 'path'],
  ['parquet', 'path'],
  ['csvFolder', 'rootFolder'],
  ['jsonFolder', 'rootFolder'],
  ['parquetFolder', 'rootFolder'],
]);

export const accessModifierNameMap = new Map<AccessModifier, string>([
  [AccessModifier.ACCESS_MODIFIER_PUBLIC, 'Public'],
  [AccessModifier.ACCESS_MODIFIER_PROJECT_ONLY, 'Project Only'],
  [AccessModifier.ACCESS_MODIFIER_PRIVATE, 'Private'],
]);
